import AgoraRTC, { IAgoraRTCClient, ILocalAudioTrack, ILocalVideoTrack } from 'agora-rtc-sdk-ng';
import { StudioLogger } from '@studio/logger/StudioLogger';
import { setupScreenShareEventListeners } from '../events/setupEventListeners';

const LOG = new StudioLogger('screenShareHelper');

/**
 * Starts screen share for an Agora attendee by creating and publishing the screen video track
 * @param props.channel - The channel ID to join for screen sharing (Broadcast ID).
 * @param props.token - The token for authentication.
 * @param props.appId - The Agora App ID from the environment variables.
 * @param props.screenShareAttendeeId - The ID of the attendee starting the screen share.
 * @param props.stream - The MediaStream to use for screen sharing.
 * @param props.screenShareClient - (Optional) An existing Agora RTC client instance. If not provided, a new client will be created.
 * @param props.onScreenShareEnded - (Optional) Callback function that will be called after Agora cleanup when browser stops screen sharing.
 * @returns An object containing the Agora RTC client, screen video track, and optional screen audio track.
 * @throws Error if no video track is found in the provided stream.
 * @remarks On publish failure, created tracks are stopped/closed and the client leaves the channel.
 */
export const startScreenShare = async ({
  channel,
  token,
  appId,
  screenShareAttendeeId,
  stream,
  screenShareClient,
  onScreenShareEnded
}: {
  channel: string;
  token: string;
  appId: string;
  screenShareAttendeeId: string;
  stream: MediaStream;
  screenShareClient?: IAgoraRTCClient;
  onScreenShareEnded?: () => void;
}): Promise<{
  client: IAgoraRTCClient;
  screenVideoTrack: ILocalVideoTrack;
  screenAudioTrack?: ILocalAudioTrack;
}> => {
  const audioStream = stream.getAudioTracks()[0];
  const videoStream = stream.getVideoTracks()[0];

  if (!videoStream) {
    throw new Error('No video track found in the provided stream for screen sharing.');
  }

  const client = screenShareClient ?? AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });

  LOG.info('Creating custom video track for screen sharing');
  const screenVideoTrack = AgoraRTC.createCustomVideoTrack({
    mediaStreamTrack: videoStream,
    optimizationMode: 'detail'
  });

  let screenAudioTrack: ILocalAudioTrack | undefined;

  if (audioStream) {
    LOG.info('Creating custom audio track for screen sharing');
    screenAudioTrack = AgoraRTC.createCustomAudioTrack({
      mediaStreamTrack: audioStream
    });
  }

  const tracks = screenAudioTrack ? [screenVideoTrack, screenAudioTrack] : [screenVideoTrack];

  try {
    await client.join(appId, channel, token, screenShareAttendeeId);

    await client.publish(tracks);
    LOG.info('Tracks published for screen sharing');
    // Set up event listener for when browser stops screen sharing
    if (onScreenShareEnded) {
      setupScreenShareEventListeners(screenVideoTrack, client, screenAudioTrack, onScreenShareEnded);
    }

    return {
      client,
      screenVideoTrack,
      screenAudioTrack
    };
  } catch (error) {
    // Cleanup for when user cancels the screen share browser prompt
    if (screenVideoTrack) {
      screenVideoTrack.stop();
      screenVideoTrack.close();
    }
    if (screenAudioTrack) {
      screenAudioTrack.stop();
      screenAudioTrack.close();
    }

    await client.leave();
    throw error;
  }
};

/**
 * Stops screen share for an Agora attendee by unpublishing the tracks
 *
 * @param screenShareClient - The Agora RTC client instance.
 * @param screenVideoTrack - The screen video track to be stopped.
 * @param screenAudioTrack - (Optional) The screen audio track to be stopped.
 */
export async function stopScreenShare(
  screenShareClient: IAgoraRTCClient,
  screenVideoTrack: ILocalVideoTrack,
  screenAudioTrack?: ILocalAudioTrack
): Promise<void> {
  const tracks = screenAudioTrack ? [screenVideoTrack, screenAudioTrack] : [screenVideoTrack];

  if (screenVideoTrack) {
    screenVideoTrack.stop();
    screenVideoTrack.close();
  }

  if (screenAudioTrack) {
    screenAudioTrack.stop();
    screenAudioTrack.close();
  }

  // Only unpublish and leave if client exists
  if (screenShareClient) {
    await screenShareClient.unpublish(tracks);
    await screenShareClient.leave();
  }
}
